import { postJSON } from "../api.js";
import { esc } from "../ui/format.js";
import { uiCard, uiEmpty, uiGet, uiHeader, uiMoney } from "../ui/kit.js";
import { toast } from "../ui/modals.js";

/**
 * Nemesis (backend/nemesis.py): your rival CEO, face to face.
 *
 *   the portrait   a face drawn from their seed (same face every visit)
 *   the feud       a meter with the four lines that escalate the rivalry
 *   their move     the letter waiting for your answer; each choice is a
 *                  real action (execute_action "nemesis_respond")
 *   head to head   their company against yours
 *   memory         what they remember you did, newest first
 */

const NM_SKIN = ["#f1c7a5", "#d9a47c", "#b98057", "#8d5a3b", "#5f3b26", "#e8b996"];
const NM_HAIR = ["#1d1a1a", "#3b2a1f", "#6b4426", "#a8783f", "#c9c3b8", "#7a1f1f"];
const NM_SUIT = ["#1b2140", "#2a1f3d", "#14302c", "#3a1c1c", "#22262e"];

/** A seeded portrait: same seed, same face. Pure SVG. */
function nmFace(seed, size = 140) {
  let s = seed >>> 0 || 1;
  const r = () => { s = (s * 1103515245 + 12345) >>> 0; return (s >>> 8) / 16777216; };
  const pick = (a) => a[Math.floor(r() * a.length)];
  const skin = pick(NM_SKIN), hair = pick(NM_HAIR), suit = pick(NM_SUIT);
  const jaw = 30 + r() * 8, brow = r() > 0.5, beard = r() > 0.7, glasses = r() > 0.6, hairStyle = Math.floor(r() * 4);
  const eyeY = 62 + r() * 4, smirk = (r() - 0.3) * 6;
  const hairPath = [
    `M28,58 Q30,18 70,16 Q110,18 112,58 Q104,34 70,32 Q36,34 28,58Z`,
    `M26,64 Q22,14 70,14 Q118,14 114,64 Q110,40 96,36 Q70,44 44,36 Q30,42 26,64Z`,
    `M30,52 Q40,20 70,20 Q100,20 110,52 L104,44 Q70,28 36,44Z`,
    `M24,70 Q20,10 70,12 Q120,10 116,70 L112,96 Q114,40 70,34 Q26,40 28,96Z`,
  ][hairStyle];
  return `<svg class="nm-face" viewBox="0 0 140 160" width="${size}" height="${size * 1.14}" role="img" aria-label="Portrait">
    <defs><radialGradient id="nmbg${seed}" cx=".5" cy=".4" r=".7"><stop offset="0" stop-color="#3a1d2c"/><stop offset="1" stop-color="#0d0a14"/></radialGradient></defs>
    <rect width="140" height="160" rx="16" fill="url(#nmbg${seed})"/>
    <path d="M14,160 Q18,118 70,112 Q122,118 126,160Z" fill="${suit}"/>
    <path d="M58,114 L70,140 L82,114Z" fill="#f2f2f2"/><path d="M66,118 L70,150 L74,118Z" fill="#8a1c2c"/>
    <rect x="58" y="96" width="24" height="22" rx="8" fill="${skin}" filter="brightness(.9)"/>
    <ellipse cx="70" cy="66" rx="${jaw}" ry="40" fill="${skin}"/>
    ${beard ? `<path d="M${70 - jaw + 4},74 Q70,${118} ${70 + jaw - 4},74 Q70,96 ${70 - jaw + 4},74Z" fill="${hair}" opacity=".85"/>` : ""}
    <path d="${hairPath}" fill="${hair}"/>
    <ellipse cx="56" cy="${eyeY}" rx="4" ry="${brow ? 2.6 : 3.2}" fill="#1a1414"/><ellipse cx="84" cy="${eyeY}" rx="4" ry="${brow ? 2.6 : 3.2}" fill="#1a1414"/>
    <path d="M47,${eyeY - 9} L63,${eyeY - (brow ? 5 : 8)} M93,${eyeY - 9} L77,${eyeY - (brow ? 5 : 8)}" stroke="${hair}" stroke-width="3.2" stroke-linecap="round"/>
    ${glasses ? `<g fill="none" stroke="#c9d2ea" stroke-width="2"><rect x="45" y="${eyeY - 8}" width="22" height="15" rx="5"/><rect x="73" y="${eyeY - 8}" width="22" height="15" rx="5"/><path d="M67,${eyeY - 2} L73,${eyeY - 2}"/></g>` : ""}
    <path d="M70,${eyeY + 4} L66,${eyeY + 20} L72,${eyeY + 21}" fill="none" stroke="rgba(0,0,0,.25)" stroke-width="2"/>
    <path d="M58,${eyeY + 32} Q70,${eyeY + 34 + smirk} 84,${eyeY + 29 - smirk}" fill="none" stroke="#5a2424" stroke-width="2.6" stroke-linecap="round"/>
  </svg>`;
}

/** The feud meter: 0..100 with the lines where the rivalry escalates. */
function nmFeudMeter(n) {
  const W = 640, x = (v) => 20 + ((W - 40) * v) / 100;
  const marks = n.stages.slice(1).map((s, i) => {
    const on = n.stage >= i + 1;
    return `<g><line x1="${x(s.at)}" x2="${x(s.at)}" y1="22" y2="56" class="nm-mark${on ? " on" : ""}"/>
      <text x="${x(s.at)}" y="16" text-anchor="middle" class="nm-mark-t${on ? " on" : ""}">${esc(s.label)}</text>
      <text x="${x(s.at)}" y="72" text-anchor="middle" class="nm-mark-v">${s.at}</text></g>`;
  }).join("");
  return `<svg class="nm-meter" viewBox="0 0 ${W} 80" role="img" aria-label="Feud ${n.feud} of 100">
    <defs><linearGradient id="nm-heat" x1="0" x2="1"><stop offset="0" stop-color="#fbbf24"/><stop offset=".55" stop-color="#fb7185"/><stop offset="1" stop-color="#e11d48"/></linearGradient></defs>
    <rect x="20" y="30" width="${W - 40}" height="18" rx="9" class="nm-track"/>
    <rect x="20" y="30" width="${Math.max(6, x(n.feud) - 20).toFixed(1)}" height="18" rx="9" fill="url(#nm-heat)" class="nm-fill"/>
    ${marks}
    <circle cx="${x(n.feud).toFixed(1)}" cy="39" r="11" class="nm-knob"/><text x="${x(n.feud).toFixed(1)}" y="43" text-anchor="middle" class="nm-knob-t">${Math.round(n.feud)}</text>
  </svg>`;
}

function nmMove(n) {
  const m = n.open_move;
  if (!m) {
    const next = n.next_stage;
    return uiEmpty("No move to answer", next ? `Quiet, for now. At ${next.at} on the feud meter: ${next.label.toLowerCase()} (${next.what.toLowerCase()})` : "They've thrown everything they have.");
  }
  return `<article class="nm-letter">
    <div class="nm-letter-kick">${esc(n.stages.find((s) => s.key === m.stage).label)} · month ${m.month} · answer this month</div>
    <h3>${esc(m.title)}</h3>
    <blockquote>“${esc(m.quote)}”<cite>${esc(n.ceo.name)}, ${esc(n.rival.name)}</cite></blockquote>
    <p>${esc(m.body)}</p>
    <div class="nm-options">${m.options.map((o) => `
      <button type="button" class="nm-opt" data-nm="${esc(o.key)}" ${o.affordable === false ? "disabled" : ""}>
        <b>${esc(o.label)}</b><span>${esc(o.detail)}</span>
        <em>${o.cost ? uiMoney(o.cost) : "Free"}${o.odds ? ` · ${o.odds}% odds` : ""}${o.affordable === false ? " · can't afford" : ""}</em></button>`).join("")}</div>
    <p class="nm-warn">Leave it unanswered and it resolves itself next month, badly.</p>
  </article>`;
}

function nmHeadToHead(n) {
  const row = (label, a, b, fmt) => {
    const max = Math.max(1, Math.abs(a || 0), Math.abs(b || 0));
    return `<div class="nm-h2h-row"><span class="nm-h2h-v you">${fmt(a)}</span>
      <div class="nm-h2h-bars"><i class="you" style="width:${(50 * Math.abs(a || 0) / max).toFixed(1)}%"></i><b>${esc(label)}</b><i class="them" style="width:${(50 * Math.abs(b || 0) / max).toFixed(1)}%"></i></div>
      <span class="nm-h2h-v them">${fmt(b)}</span></div>`;
  };
  const m = (v) => uiMoney(v || 0, { compact: true });
  return `<div class="nm-h2h"><div class="nm-h2h-head"><span>${esc(n.you.name)}</span><span>${esc(n.rival.name)}</span></div>
    ${row("Monthly revenue", n.you.monthly_revenue, n.rival.monthly_revenue, m)}
    ${row("Brand / reputation", n.you.brand, n.rival.reputation, (v) => `${Math.round(v || 0)}`)}
    ${row("Aggression", 50, n.rival.aggression, (v) => `${Math.round(v || 0)}`)}
    <p class="sx-card-sub">Their market share ${(n.rival.market_share_pct || 0).toFixed(1)}% (you win the rivalry under 2%) · cash ${m(n.rival.cash)} · latest: ${esc(n.rival.last_action || "—")}</p></div>`;
}

async function renderNemesis(main) {
  const n = await uiGet("/nemesis");
  if (!n.ceo) {
    main.innerHTML = `<div class="sx nm">${uiHeader("Nemesis", "One rival CEO who decides you are the one to beat.")}
      ${uiCard("", uiEmpty("No nemesis yet", n.note || "A rival will come for you soon."))}</div>`;
    return;
  }
  const rec = n.record || { won: 0, lost: 0, truces: 0 };
  main.innerHTML = `
    <div class="sx nm">
      ${uiHeader("Nemesis", "Your rival's CEO has a face, a temper and a long memory. The feud builds every month; each line it crosses brings a new move. Answer it, or it answers itself.")}
      <section class="nm-hero">
        <div class="nm-portrait">${nmFace(n.ceo.face_seed)}</div>
        <div class="nm-id">
          <span class="nm-kicker">${n.status === "beaten" ? "Former nemesis · beaten" : `Your nemesis since month ${n.ceo.since_month}`}</span>
          <h2>${esc(n.ceo.name)}</h2>
          <div class="nm-sub">${esc(n.ceo.title)}, <b>${esc(n.rival.name)}</b></div>
          <div class="nm-persona"><b>${esc(n.ceo.personality || "")}</b> ${esc(n.ceo.tagline || "")}</div>
          <div class="nm-record"><span><b>${rec.won}</b> won</span><span><b>${rec.lost}</b> lost</span><span><b>${rec.truces}</b> truces</span><span class="nm-stage">${esc(n.stage_label)}</span></div>
        </div>
      </section>
      ${uiCard("The feud", nmFeudMeter(n), { sub: n.rules })}
      <div class="nm-grid">
        <div>${uiCard("Their move", nmMove(n))}</div>
        <div>${uiCard("Head to head", nmHeadToHead(n))}
          ${uiCard("What they remember", `<ol class="nm-memory">${n.memory.slice(0, 10).map((m) => `<li class="nm-m-${esc(m.kind)}"><b>M${m.month}</b><span>${esc(m.text)}</span></li>`).join("")}</ol>`)}</div>
      </div>
      ${n.past.length ? uiCard("Old rivals", `<div class="nm-past">${n.past.map((p) => `<span>${esc(p.ceo)} (${esc(p.company)}) · M${p.from}–M${p.to} · ${esc(p.outcome)}</span>`).join("")}</div>`) : ""}
    </div>`;
  main.querySelectorAll("[data-nm]").forEach((b) => b.addEventListener("click", async () => {
    main.querySelectorAll("[data-nm]").forEach((x) => { x.disabled = true; });
    try {
      const r = await postJSON("/actions/execute", { action: { type: "nemesis_respond", params: { option: b.dataset.nm } } });
      toast(r.message);
    } catch (e) { toast(e.message || "That didn't work.", true); }
    await window.refreshAll();
  }));
}

window.renderNemesis = renderNemesis;

export { nmFace, renderNemesis };
